import type { LinksFunction } from "react-router";
import { absImage, clean, prefer } from '.';
import { generateCanonicalLinks } from "@forge42/seo-tools/canonical";
type FileLite = { id: string; name: string; path: string };

export type SeoLike = {
  title?: string;
  meta_description?: string;
  focus_keyphrase?: string;
  og_image?: FileLite[];
};
export type SeoOptions = {
  title?: string;
  description?: string;
  image?: string;
  siteName?: string;
  type?: "website" | "article";
  robots?: string;
  url?: string; // absolute URL
  twitter?: {
    card?: "summary_large_image" | "summary";
    site?: string;
    creator?: string;
  };
  keywords?: string;
};


export type EntityWithSeo = {
  title?: string;
  description?: string;
  slug?: string;
  thumbnail?: FileLite[];
  seo?: SeoLike;
  //  tags?: { name: string }[]
};
export function getSeoRequest(request: Request) {
  const url = new URL(request.url);
  const origin = getOrigin(request);
  const canonicalUrl = origin
    ? `${origin}${url.pathname}${url.search}`
    : `${url.pathname}${url.search}`;
  return {
    canonical: canonicalUrl,
    origin
  }
}
export function getOrigin(request: Request) {


  try {
    const url = new URL(request.url);
    return url.origin;
  } catch {
    return "";
  }
}

export function buildSeoMeta(opts: SeoOptions) {
  const title = `${opts.title ? opts.title + " | " : ""} Kland`;
  const description = opts.description ?? "Kland Kết nối công nghệ kiến tạo tương lại";
  const image = opts.image ?? "/image/landing_page/Banner_1.png";
  const type = opts.type ?? "website";
  const robots = opts.robots ?? "follow, index , max-snippet:-1, max-video-preview:-1, max-image-preview:large";
  const url = opts.url ?? "";
  const siteName = opts.siteName ?? "Kland";
  const twitterCard = opts.twitter?.card ?? "summary_large_image";

  return [
    { title },
    description ? { name: "description", content: description } : null,
    robots ? { name: "robots", content: robots } : null,
    type ? { property: "og:type", content: type } : null,
    title ? { property: "og:title", content: title } : null,
    description ? { property: "og:description", content: description } : null,
    url ? { property: "og:url", content: url } : null,
    siteName ? { property: "og:site_name", content: siteName } : null,
    image ? { property: "og:image", content: image } : null,
    twitterCard ? { name: "twitter:card", content: twitterCard } : null,
    title ? { name: "twitter:title", content: title } : null,
    description ? { name: "twitter:description", content: description } : null,
    image ? { name: "twitter:image", content: image } : null,
    opts.keywords ? { name: "keywords", content: opts.keywords } : null,
  ].filter(Boolean) as any[];
}

export function buildCanonicalLink(href: string): ReturnType<LinksFunction>[number] {
  return { rel: "canonical", href } as any;
}
// New helper using @forge42/seo-tools (prefer this over buildCanonicalLink)
export function buildCanonicalTags(domain: string, path: string) {
  return generateCanonicalLinks(
    {
      domain,
      url: path,
      alternatives: [],
    },
    true
  );
}


export type BuildMetaDeps = {
  hostName: (path: string) => string;         // bắt buộc
  buildSeoMeta: (opts: any) => any[];         // bắt buộc (hàm bạn đã có)
  siteName?: string;                          // mặc định "Kland"
  separator?: string;                         // mặc định "|"
  defaultImage?: string;                      // fallback cuối cùng
  baseUrl?: string;                           // để tạo absolute url/canonical
};
export function resolveSeoFromEntity(entity: EntityWithSeo, deps: BuildMetaDeps) {
  const seo = entity?.seo ?? {};
  const title = prefer(seo.title, entity.title);
  const description = prefer(seo.meta_description, entity.description);

  const imagePath = prefer(
    seo.og_image?.[0]?.path,
    entity.thumbnail?.[0]?.path
  );

  const imageAbs = absImage(deps.hostName, imagePath) ?? deps.defaultImage;

  const keywords = clean(seo.focus_keyphrase);

  const url = entity.slug && deps.baseUrl
    ? `${deps.baseUrl.replace(/\/+$/, "")}/${entity.slug.replace(/^\/+/, "")}`
    : undefined;

  return { title, description, image: imageAbs, keywords, url, canonical: url };
}

export function buildMetaFromLoader<LD>(
  args: { loaderData?: LD },
  deps: BuildMetaDeps,
  pick: (ld: LD | undefined) => EntityWithSeo | undefined
) {
  const entity = pick(args.loaderData) || {};
  const { title, description, image, keywords, url, canonical } = resolveSeoFromEntity(entity, deps);

  return deps.buildSeoMeta({
    title,               // chỉ PAGE/POST title; buildSeoMeta sẽ format "[Page] | [Site]"
    description,
    image,
    keywords,
    url,
    canonical,
    siteName: deps.siteName ?? "Kland",
    separator: deps.separator ?? "|",
    locale: "vi_VN",
  });
}
